import { isDefined } from "@clipboard-health/util-ts";
import { useGetQuery, type UseGetQueryOptions } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";

import { type WorkerStatusResponse, workerStatusResponseSchema } from "../types";

const WORKER_HOME_HEALTH_ENABLED_URL = `${environmentConfig.REACT_APP_HOME_HEALTH_API_URL}/v1/:workplaceId/workers/:workerId`;

/**
 * The worker ID intentionally fills both URL segments. The Home Health API does not use or check
 * `:workplaceId` for this endpoint (authorization is employee-based), and there is no real
 * workplace ID in the admin context this runs in — `useCurrentUserId()` was removed here in
 * PR #2387 (GRO-266) because it returned invalid values from admin sessions.
 */
export function getWorkerHomeHealthEnabledUrl(workerId: string): string {
  return WORKER_HOME_HEALTH_ENABLED_URL.replace(":workplaceId", workerId).replace(
    ":workerId",
    workerId
  );
}

export function getWorkerHomeHealthEnabledQueryKey(workerId: string) {
  return [getWorkerHomeHealthEnabledUrl(workerId), null];
}

export function useIsWorkerHomeHealthEnabled(
  workerId: string,
  options: UseGetQueryOptions<WorkerStatusResponse> = {}
) {
  return useGetQuery({
    url: getWorkerHomeHealthEnabledUrl(workerId),
    responseSchema: workerStatusResponseSchema,
    retry: 0,
    staleTime: 1000 * 60 * 5,
    refetchInterval: 1000 * 60 * 10,
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.HOME_HEALTH_GET_WORKER_ENABLED_FAILURE,
    },
    ...options,
    enabled: isDefined(workerId) && typeof workerId === "string" && (options.enabled ?? true),
  });
}
